/**
 * Slice 45 — spend against outcome, per surface: how many each surface
 * reached in the period, and what the ad surfaces spent to reach them. The
 * comparison the owner asked for, in the only form this product can stand
 * behind — `surface-comparison.ts` argues each half of it, and the Results by
 * surface workbook's first sheet is the same rows.
 *
 * ## EACH TILE COUNTS IN ITS OWN UNIT, AND NOTHING ADDS OR SCALES THEM
 *
 * A message tile's figure is the distinct addresses its campaigns messaged —
 * printed with the analytics contract's own unit, as "Who was reached" above
 * prints it — and an ad tile's is the people Meta showed the ads to. They sit
 * side by side as numbers and nothing more: no total, and no bar. A bar would
 * put addresses and Meta's audience on one axis — two populations in one
 * column, which the reach ledger refuses — and the Meta ad account card above
 * already draws these same reaches as numbers, never bars.
 *
 * ## MONEY WHERE THERE IS MONEY, "NOT MEASURED" WHERE WEND CANNOT SEE IT
 *
 * An ad tile carries its spend and its spend per 1,000 people reached; the ad
 * tiles share a currency, so those compare. A message tile's spend is the
 * "Not measured" chip: the workspace's own AWS and Meta accounts bill it, and
 * a credit is not that money. No per-thousand figure where there is nothing
 * to divide — never a dash. The figures sit in the Meta panels' own stacked
 * label style (`PanelFigure`), so the two cards read as one family; and an ad
 * tile the stub answered says "Simulated", as the card above does.
 *
 * ## ONLY WHEN SOMETHING WAS SPENT, AND ONLY FOR ONE PERIOD
 *
 * Drawn only when an ad row carries spend above zero: with none — no account
 * read, or an account idle all period — the tiles would restate "Who was
 * reached" above, or set zeros against it. A message surface that sent
 * nothing in the period has no tile at all (the builder's rule), so an
 * ads-only period draws the ad tiles alone and never a zero for email. The
 * builder gives ad rows only when the ad read covers the report's own days,
 * so while a new period is read — the report and the ad read landing apart —
 * the card is not drawn until both cover one period, rather than setting one
 * period's spend against another's reach. Its days' zone is the Meta ad
 * account card's tag, directly above; nothing here repeats it.
 *
 * Pure, like `AnalyticsAds`: the page hands the report and the ad read in.
 */
import { Card, CardContent, CardHeader, CardTitle, StateChip } from '@wend/ui';
import type { ReactNode } from 'react';

/* `@wend/shared` directly, never through the API client. */
import {
  AD_REACH_UNIT,
  MESSAGE_REACH_UNIT,
  surfaceComparison,
  type AnalyticsReport,
  type MetaAdsPeriodState,
  type SurfaceComparisonRow,
} from '@wend/shared';

import { formatCount } from '../../lib/format';
import { placementLabel, surfaceLabel } from '../../lib/surface-display';
import { formatMetaMoney } from '../integrations/MetaAdsAccountRead';
import { PanelFigure } from '../meta-ads/MetaAdSurfaces';

export interface AnalyticsBySurfaceProps {
  readonly report: AnalyticsReport;
  /** The ad read, or `null` when there is none to draw — not connected, not answered, or failed. */
  readonly ads: MetaAdsPeriodState | null;
}

function money(minor: number | null, currency: string): ReactNode {
  if (minor === null) return <StateChip state="not_measured" label="Not measured" />;
  return formatMetaMoney(minor, currency) ?? <StateChip state="not_measured" label={currency} />;
}

function title(row: SurfaceComparisonRow): string {
  return row.surface === null ? placementLabel('other') : surfaceLabel(row.surface);
}

function reached(row: SurfaceComparisonRow): number | null {
  return row.kind === 'message' ? row.reach.addresses : row.people;
}

export function AnalyticsBySurface({ report, ads }: AnalyticsBySurfaceProps) {
  if (ads === null) return null;
  const rows = surfaceComparison({ analytics: report, ads });
  if (!rows.some((row) => row.kind === 'ad' && row.spend !== null && row.spend > 0)) return null;

  return (
    <Card data-role="analytics-by-surface">
      <CardHeader>
        <CardTitle>By surface</CardTitle>
      </CardHeader>
      <CardContent>
        {/* A grid, not a wrap: a wrapped last tile stretched across a row reads as the largest surface.
            Each tile's four lines sit on rows its whole row of tiles shares, so a title that wraps —
            "WhatsApp Status ads" beside "Simulated" — moves that row's figures together, not one alone. */}
        <ul data-role="by-surface-rows" className="grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-3">
          {rows.map((row) => {
            const count = reached(row);
            return (
              <li
                key={row.surface ?? 'other'}
                data-role="by-surface-row"
                data-surface={row.surface ?? 'other'}
                className="row-span-4 grid min-w-0 grid-rows-subgrid gap-2 rounded-base border border-line bg-white/[0.02] p-4"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <h3 className="font-mono text-[0.62rem] uppercase tracking-[0.12em] text-muted">{title(row)}</h3>
                  {row.kind === 'ad' && row.simulated && <StateChip state="estimated" label="Simulated" />}
                </span>
                <span className="text-[1.5rem] font-semibold leading-none tracking-[-0.03em] tabular-nums text-text">
                  {count === null ? <StateChip state="not_measured" label="Not measured" /> : formatCount(count)}
                </span>
                <span className="font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
                  {row.kind === 'message' ? MESSAGE_REACH_UNIT : AD_REACH_UNIT}
                </span>
                <dl className="mt-1 grid grid-cols-2 gap-x-6 gap-y-3">
                  <PanelFigure label="Spend">
                    {row.kind === 'message' ? <StateChip state="not_measured" label="Not measured" /> : money(row.spend, row.currency)}
                  </PanelFigure>
                  {row.kind === 'ad' && row.perThousand !== null && (
                    <PanelFigure label="Per 1,000 reached">{money(row.perThousand, row.currency)}</PanelFigure>
                  )}
                </dl>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
